import type { FC } from 'react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { FileUploadInfo } from '@wix/da-shared-react/pkg/redux/uploads/types';
import UploadedFile from '@wix/da-shared-react/pkg/FileUploadField/UploadedFile';
import Dumbo from '@wix/da-shared-react/pkg/DeviationViews/Dumbo';
import { mimeTypeToPcpType } from '@wix/da-shared-react/pkg/utils/mime';
import {
  ThemeOverride,
  ThemeSurface,
} from '@wix/da-react-context/pkg/ThemeContext';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import CloseIcon from '@wix/da-ds/pkg/Icons/24x24/Close';
import { CompletedUploadWithDeviation } from '../../types/store';

import s from './StudioUploadsList.scss';

export interface Props {
  activeUploads: FileUploadInfo[];
  completedUploads: CompletedUploadWithDeviation[];
  shouldCloseDialog?: boolean;
  clearInactiveUploads: () => void;
  cancelFileUpload: ({ uploadId }) => void;
}

const StudioUploadsList: FC<Props> = ({
  activeUploads,
  completedUploads,
  shouldCloseDialog,
  clearInactiveUploads,
  cancelFileUpload,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const failedUploadsCount = useMemo(
    () => activeUploads.filter(u => !!u.error).length,
    [activeUploads]
  );
  const isUploading = activeUploads.length > failedUploadsCount;

  // When a new upload is received, open dialog
  useEffect(() => {
    setIsOpen(activeUploads.length > 0 || completedUploads.length > 0);
  }, [activeUploads.length, completedUploads.length]);

  // When a toaster notification is pushed, close the dialog
  useEffect(() => {
    if (shouldCloseDialog) {
      setIsOpen(false);
    }
  }, [shouldCloseDialog]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    clearInactiveUploads();
  }, [clearInactiveUploads]);

  if (!isOpen) {
    return null;
  }

  return (
    <ThemeOverride
      themeSurface={ThemeSurface.TERTIARY}
      tag="div"
      className={c(s['root'], 'ds-surface-tertiary')}
    >
      <div className={s['header']}>
        <div className={s['title']}>
          {isUploading
            ? t('studio.uploads.header.uploading')
            : t('studio.uploads.header.uploadCount', {
                count: completedUploads.length,
              })}
        </div>
        {failedUploadsCount > 0 && (
          <span className={s['failed-uploads']}>
            {t('studio.uploads.header.failedCount', {
              count: failedUploadsCount,
            })}
          </span>
        )}
        <IconButton
          icon={CloseIcon}
          size="small"
          onClick={handleClose}
          aria-label={t('common.close')}
        />
      </div>
      <div className={s['list']}>
        {activeUploads.map(item => (
          <UploadedFile
            key={item.uploadId}
            name={item.file.name}
            size={item.file.size}
            type={mimeTypeToPcpType(item.file.type)}
            uploadProgress={item.progress}
            onRemove={() => cancelFileUpload({ uploadId: item.uploadId })}
            status={item.error ? 'error' : 'loading'}
            errorMessage={item.error ? (item.error.cause as string) : undefined}
          />
        ))}
        {completedUploads.map(item => (
          <UploadedFile
            key={item.uploadId}
            name={item.file.name}
            size={item.file.size}
            type={mimeTypeToPcpType(item.file.type)}
            status="complete"
            preview={
              item.deviation && (
                <Dumbo deviation={item.deviation} width={20} height={20} />
              )
            }
          />
        ))}
      </div>
    </ThemeOverride>
  );
};
StudioUploadsList.displayName = 'StudioUploadsList';

export default StudioUploadsList;
